/**
 * AI onboarding state (Zustand).
 *
 * Three phases: compose (prompt chat box) → generating (theater) → result
 * (interactive preview + rails + create). The draft — everything needed to
 * resume — persists to localStorage so the Google sign-in round-trip (or an
 * accidental refresh) never loses progress. The server-side OnboardingDraft
 * is the source of truth for the generated design; this store only mirrors
 * what the client needs to render and edit.
 */

import { create } from 'zustand'
import type {
  BoothSpecDto,
  DraftFrame,
  GenerateResponse,
  WelcomeDesignDto,
  OnboardingMode
} from './onboardingApi'

export type OnboardingPhase = 'compose' | 'generating' | 'result'
export type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
export type PreviewOrientation = 'portrait' | 'landscape'
export type AttachmentRole = 'auto' | 'literal' | 'style'

export interface ComposerAttachment {
  dataUrl: string
  name: string
  role: AttachmentRole
}

/** Result-phase slider steps (compose is phase-level, not a step). */
export const RESULT_STEPS = ['backgrounds', 'basics', 'frames', 'filters', 'effects'] as const
export type ResultStep = (typeof RESULT_STEPS)[number]

const STORAGE_KEY = 'db.onboardingDraft.v1'
/**
 * The compose-phase scratch key. Deliberately separate from the draft: it is
 * written BEFORE a draft exists (the draft key can't be — `persist()` needs a
 * draftId and a spec), so the prompt someone spent two minutes writing
 * survives a refresh that happens before they ever press Build.
 *
 * Attachments and the logo are intentionally NOT stored here: they are data
 * URLs and would blow the ~5MB localStorage quota on the first photo.
 */
const COMPOSE_KEY = 'db.onboardingCompose.v1'
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000 // matches the server draft TTL

const SLUGIFY_RE = /[^a-z0-9]+/g

/**
 * The booth's own drawn frames carry synthetic ids: the art exists on the
 * draft, but the Frame document does not until create time, when the server
 * materialises and attaches it. These ids must never reach the create call.
 */
export const DRAWN_FRAME_PREFIX = 'drawn:'

export function isDrawnFrameId(id: string): boolean {
  return id.startsWith(DRAWN_FRAME_PREFIX)
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(SLUGIFY_RE, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

interface PersistedDraft {
  ts: number
  draftId: string
  prompt: string
  spec: BoothSpecDto | null
  assets: GenerateResponse['assets'] | null
  welcomeDesign: WelcomeDesignDto | null
  name: string
  slug: string
  frameIds: string[]
  filterIds: string[]
  captureMode: 'standard' | 'frame-based' | null
  aiEffectId: string | null
  remaining: GenerateResponse['remaining'] | null
  /** Which step the creator was on. Restoring elsewhere reads as data loss. */
  stepIndex: number
  draftFrames: DraftFrame[]
}

interface PersistedCompose {
  ts: number
  prompt: string
  slug: string
}

function loadPersisted(): PersistedDraft | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedDraft
    if (!parsed?.draftId || !parsed.spec) return null
    if (Date.now() - (parsed.ts || 0) > DRAFT_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function loadCompose(): PersistedCompose | null {
  try {
    const raw = window.localStorage.getItem(COMPOSE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedCompose
    if (!parsed || (!parsed.prompt && !parsed.slug)) return null
    if (Date.now() - (parsed.ts || 0) > DRAFT_TTL_MS) {
      window.localStorage.removeItem(COMPOSE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function clearStorage(): void {
  for (const key of [STORAGE_KEY, COMPOSE_KEY]) {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // ignore
    }
  }
}

interface OnboardingState {
  phase: OnboardingPhase

  // Compose
  prompt: string
  logoDataUrl: string | null
  logoName: string | null
  attachments: ComposerAttachment[]

  // Result slider position
  stepIndex: number

  // Generation result
  draftId: string | null
  spec: BoothSpecDto | null
  assets: GenerateResponse['assets'] | null
  welcomeDesign: WelcomeDesignDto | null
  remaining: GenerateResponse['remaining'] | null

  // Editable result fields
  name: string
  slug: string
  slugStatus: SlugStatus
  captureMode: 'standard' | 'frame-based' | null
  aiEffectId: string | null
  frameIds: string[]
  filterIds: string[]
  /** The booth's own drawn frames (art only until create time). */
  draftFrames: DraftFrame[]

  // UI
  previewOrientation: PreviewOrientation
  generating: boolean
  regenInFlight: string | null
  submitting: boolean
  error: string | null
  /** True when a persisted draft was restored on load. */
  restored: boolean

  // Actions
  setPhase: (phase: OnboardingPhase) => void
  setPrompt: (prompt: string) => void
  setLogo: (dataUrl: string | null, name: string | null) => void
  addAttachment: (a: ComposerAttachment) => void
  removeAttachment: (index: number) => void
  setAttachmentRole: (index: number, role: AttachmentRole) => void
  setStepIndex: (i: number) => void
  nextStep: () => void
  prevStep: () => void
  startGenerating: () => void
  applyGeneration: (res: GenerateResponse) => void
  /** Regen result: refresh assets/design/caps but keep the user's edits. */
  applyRegen: (res: GenerateResponse) => void
  generationFailed: (message: string) => void

  setName: (name: string) => void
  setSlug: (slug: string) => void
  setSlugStatus: (status: SlugStatus) => void
  setCaptureMode: (mode: 'standard' | 'frame-based') => void
  setAiEffectId: (id: string | null) => void
  toggleFrame: (id: string) => void
  toggleFilter: (id: string) => void
  setFrameIds: (ids: string[]) => void
  setFilterIds: (ids: string[]) => void
  setDraftFrames: (frames: DraftFrame[]) => void

  setPreviewOrientation: (o: PreviewOrientation) => void
  setRegenInFlight: (target: string | null) => void
  setSubmitting: (v: boolean) => void
  setError: (e: string | null) => void

  dismissRestored: () => void

  // Derived
  primaryMode: () => OnboardingMode
  hasUnsavedWork: () => boolean
  persist: () => void
  persistCompose: () => void
  discardDraft: () => void
  startOver: () => void
}

const emptyState = {
  phase: 'compose' as OnboardingPhase,
  prompt: '',
  logoDataUrl: null as string | null,
  logoName: null as string | null,
  attachments: [] as ComposerAttachment[],
  stepIndex: 0,
  draftId: null as string | null,
  spec: null as BoothSpecDto | null,
  assets: null as GenerateResponse['assets'] | null,
  welcomeDesign: null as WelcomeDesignDto | null,
  remaining: null as GenerateResponse['remaining'] | null,
  name: '',
  slug: '',
  slugStatus: 'idle' as SlugStatus,
  captureMode: null as 'standard' | 'frame-based' | null,
  aiEffectId: null as string | null,
  frameIds: [] as string[],
  filterIds: [] as string[],
  draftFrames: [] as DraftFrame[],
  previewOrientation: 'portrait' as PreviewOrientation,
  generating: false,
  regenInFlight: null as string | null,
  submitting: false,
  error: null as string | null,
  restored: false
}

function initialState(): typeof emptyState {
  const saved = loadPersisted()
  if (!saved) {
    // No generated booth, but there may still be an unsent prompt worth more
    // to its author than anything else on the page.
    const compose = loadCompose()
    if (!compose) return emptyState
    return {
      ...emptyState,
      prompt: compose.prompt || '',
      slug: compose.slug || '',
      restored: Boolean(compose.prompt)
    }
  }
  return {
    ...emptyState,
    phase: 'result',
    prompt: saved.prompt,
    stepIndex: clampStep(saved.stepIndex),
    draftId: saved.draftId,
    spec: saved.spec,
    assets: saved.assets,
    welcomeDesign: saved.welcomeDesign,
    remaining: saved.remaining,
    name: saved.name,
    slug: saved.slug,
    captureMode: saved.captureMode,
    aiEffectId: saved.aiEffectId,
    frameIds: saved.frameIds || [],
    filterIds: saved.filterIds || [],
    draftFrames: saved.draftFrames || [],
    restored: true
  }
}

function clampStep(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0
  return Math.max(0, Math.min(RESULT_STEPS.length - 1, Math.trunc(n)))
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  ...initialState(),

  setPhase: (phase) => set({ phase }),
  setPrompt: (prompt) => {
    set({ prompt })
    get().persistCompose()
  },
  setLogo: (logoDataUrl, logoName) => set({ logoDataUrl, logoName }),
  addAttachment: (a) => set((s) => ({ attachments: [...s.attachments, a].slice(0, 3) })),
  removeAttachment: (index) =>
    set((s) => ({ attachments: s.attachments.filter((_, i) => i !== index) })),
  setAttachmentRole: (index, role) =>
    set((s) => ({
      attachments: s.attachments.map((a, i) => (i === index ? { ...a, role } : a))
    })),
  setStepIndex: (stepIndex) => {
    set({ stepIndex: clampStep(stepIndex) })
    get().persist()
  },
  nextStep: () => {
    set((s) => ({ stepIndex: clampStep(s.stepIndex + 1) }))
    get().persist()
  },
  prevStep: () => {
    set((s) => ({ stepIndex: clampStep(s.stepIndex - 1) }))
    get().persist()
  },

  startGenerating: () => set({ phase: 'generating', generating: true, error: null }),

  applyGeneration: (res) => {
    // A slug the visitor typed into the hero headline outranks the generated
    // one — they asked for that link before they asked for the design.
    const claimed = get().slug
    set({
      phase: 'result',
      generating: false,
      regenInFlight: null,
      draftId: res.draftId,
      spec: res.spec,
      assets: res.assets,
      welcomeDesign: res.welcomeDesign,
      remaining: res.remaining,
      name: res.spec.title,
      slug: claimed || res.slug || res.spec.slugBase,
      slugStatus: 'idle',
      captureMode: res.spec.captureMode,
      error: null
    })
    get().persist()
  },

  applyRegen: (res) => {
    set({
      regenInFlight: null,
      assets: res.assets,
      welcomeDesign: res.welcomeDesign,
      remaining: res.remaining,
      error: null
    })
    get().persist()
  },

  generationFailed: (message) =>
    set((s) => ({
      // Fall back to result when an earlier generation exists, else compose.
      phase: s.spec ? 'result' : 'compose',
      generating: false,
      regenInFlight: null,
      error: message
    })),

  setName: (name) => {
    set({ name })
    get().persist()
  },
  setSlug: (slug) => {
    set({ slug })
    get().persist()
    // Also mirrored to the compose scratch: a slug claimed in the hero
    // headline is chosen before any draft exists.
    get().persistCompose()
  },
  setSlugStatus: (slugStatus) => set({ slugStatus }),
  setCaptureMode: (captureMode) => {
    set({ captureMode })
    get().persist()
  },
  setAiEffectId: (aiEffectId) => {
    set({ aiEffectId })
    get().persist()
  },
  toggleFrame: (id) => {
    set((s) => ({
      frameIds: s.frameIds.includes(id) ? s.frameIds.filter((f) => f !== id) : [...s.frameIds, id]
    }))
    get().persist()
  },
  toggleFilter: (id) => {
    set((s) => ({
      filterIds: s.filterIds.includes(id)
        ? s.filterIds.filter((f) => f !== id)
        : [...s.filterIds, id]
    }))
    get().persist()
  },
  setFrameIds: (frameIds) => {
    set({ frameIds })
    get().persist()
  },
  setDraftFrames: (draftFrames) => {
    set({ draftFrames })
    get().persist()
  },
  setFilterIds: (filterIds) => {
    set({ filterIds })
    get().persist()
  },

  setPreviewOrientation: (previewOrientation) => set({ previewOrientation }),
  setRegenInFlight: (regenInFlight) => set({ regenInFlight }),
  setSubmitting: (submitting) => set({ submitting }),
  setError: (error) => set({ error }),

  // Digital booths are phone-first; the AI flow always creates portrait-
  // primary projects (the design covers both orientations regardless).
  primaryMode: () => 'creator' as OnboardingMode,

  persist: () => {
    const s = get()
    if (!s.draftId || !s.spec) return
    const payload: PersistedDraft = {
      ts: Date.now(),
      draftId: s.draftId,
      prompt: s.prompt,
      spec: s.spec,
      assets: s.assets,
      welcomeDesign: s.welcomeDesign,
      name: s.name,
      slug: s.slug,
      frameIds: s.frameIds,
      filterIds: s.filterIds,
      captureMode: s.captureMode,
      aiEffectId: s.aiEffectId,
      remaining: s.remaining,
      stepIndex: s.stepIndex,
      draftFrames: s.draftFrames
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Storage full/blocked — the flow still works, just without resume.
    }
  },

  persistCompose: () => {
    const s = get()
    const payload: PersistedCompose = { ts: Date.now(), prompt: s.prompt, slug: s.slug }
    try {
      if (!payload.prompt && !payload.slug) window.localStorage.removeItem(COMPOSE_KEY)
      else window.localStorage.setItem(COMPOSE_KEY, JSON.stringify(payload))
    } catch {
      // Storage full/blocked — the flow still works, just without resume.
    }
  },

  dismissRestored: () => set({ restored: false }),

  /** True while there is work a refresh/close would strand. */
  hasUnsavedWork: () => {
    const s = get()
    return Boolean(s.spec) || s.prompt.trim().length > 0
  },

  discardDraft: () => {
    clearStorage()
    set({ ...emptyState })
  },

  /**
   * The deliberate counterpart to all of the above: throw everything away on
   * purpose. Same effect as discardDraft, named for what the creator means.
   */
  startOver: () => {
    clearStorage()
    set({ ...emptyState })
  }
}))
